import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TabSwitch, TabSwitchList, TabSwitchTab } from '@/ui';

import { SECTIONS } from './sections';
import { PollingControl, RefreshButton } from './shared';
import { DEFAULT_POLL_INTERVAL, PollInterval } from './types';

const EngDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pollInterval, setPollInterval] = useState<PollInterval>(DEFAULT_POLL_INTERVAL);

  // Resolve defensively: a hand-edited ?section=nonsense falls back to the
  // first section rather than blanking the page.
  const section =
    SECTIONS.find((entry) => entry.key === searchParams.get('section')) ?? SECTIONS[0];
  const tab =
    section.tabs.find((entry) => entry.key === searchParams.get('tab')) ?? section.tabs[0];

  // Params are dropped when they hold the default, so the bare URL stays clean.
  const setParam = (name: string, value: string, isDefault: boolean) =>
    setSearchParams((params) => {
      if (isDefault) {
        params.delete(name);
      } else {
        params.set(name, value);
      }
      if (name === 'section') params.delete('tab');
      return params;
    });

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Engineering Dashboard</h1>
        <div className="flex items-center gap-3">
          <RefreshButton />
          <PollingControl value={pollInterval} onChange={setPollInterval} />
        </div>
      </div>

      <TabSwitch
        variant="pills"
        selectedIndex={SECTIONS.indexOf(section)}
        onChange={(index) => setParam('section', SECTIONS[index].key, index === 0)}
        className="mb-4">
        <TabSwitchList>
          {SECTIONS.map((entry) => (
            <TabSwitchTab key={entry.key}>{entry.label}</TabSwitchTab>
          ))}
        </TabSwitchList>
      </TabSwitch>

      {section.tabs.length > 1 && (
        <TabSwitch
          variant="underline"
          selectedIndex={section.tabs.indexOf(tab)}
          onChange={(index) => setParam('tab', section.tabs[index].key, index === 0)}
          className="mb-4">
          <TabSwitchList>
            {section.tabs.map((entry) => (
              <TabSwitchTab key={entry.key}>{entry.label}</TabSwitchTab>
            ))}
          </TabSwitchList>
        </TabSwitch>
      )}

      {/* Only the active tab's panels mount, so heavier reads (unacked / history)
          poll only while their tab is open. */}
      {tab.render(pollInterval)}
    </div>
  );
};

export default EngDashboard;
